import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Car, DollarSign, Clock, TrendingUp, Users, CheckCircle, MapPin, ArrowLeft } from 'lucide-react';

const DriversPage = () => {
  const navigate = useNavigate();
  const logoUrl = 'https://customer-assets.emergentagent.com/job_transport-app-29/artifacts/c2qih6ut_J%20%26%20S%20Logo%2001%20%20%282%29_1024x1024.jpg';

  const benefits = [
    {
      icon: DollarSign,
      title: 'Great Earnings',
      description: 'Earn competitive rates with transparent pricing. Keep 85% of every ride.'
    },
    {
      icon: Clock,
      title: 'Flexible Schedule',
      description: 'Drive when you want. Set your own hours and work on your terms.'
    },
    {
      icon: TrendingUp,
      title: 'Weekly Payouts',
      description: 'Get paid weekly directly to your bank account. No waiting.'
    },
    {
      icon: Users,
      title: 'Support Team',
      description: '24/7 driver support to help you succeed on the road.'
    },
    {
      icon: CheckCircle,
      title: 'Easy Sign Up',
      description: 'Get approved in 24-48 hours and start earning immediately.'
    },
    {
      icon: MapPin,
      title: 'Work Anywhere',
      description: 'Drive in your city and surrounding areas. Maximize your earnings.'
    }
  ];

  const requirements = [
    'Valid driver license',
    'Clean driving record',
    'Vehicle 2010 or newer',
    'Insurance coverage',
    'Pass background check',
    'Smartphone (iOS or Android)'
  ];

  const steps = [
    { number: 1, title: 'Apply Online', description: 'Fill out our simple application form in minutes' },
    { number: 2, title: 'Upload Documents', description: 'Submit your license, insurance, and vehicle info' },
    { number: 3, title: 'Get Approved', description: 'We review and approve within 24-48 hours' },
    { number: 4, title: 'Start Earning', description: 'Download the app and accept your first ride' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-amber-50">
      {/* Header */}
      <header className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center gap-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/')} className="flex-shrink-0">
              <ArrowLeft size={20} />
            </Button>
            <div className="flex items-center gap-3">
              <img src={logoUrl} alt="Logo" className="h-10 w-10 object-contain flex-shrink-0" />
              <span className="text-xl md:text-2xl font-bold bg-gradient-to-r from-amber-600 to-yellow-500 bg-clip-text text-transparent whitespace-nowrap">J-Style Services</span>
            </div>
          </div>
          <div className="flex gap-2 md:gap-3 flex-shrink-0">
            <Button variant="outline" onClick={() => navigate('/driver/login')} className="border-slate-700 text-slate-700 hover:bg-slate-50 text-sm md:text-base whitespace-nowrap">
              Sign In
            </Button>
            <Button onClick={() => navigate('/driver/signup')} className="bg-gradient-to-r from-amber-500 to-yellow-600 hover:from-amber-600 hover:to-yellow-700 text-white text-sm md:text-base whitespace-nowrap">
              Apply Now
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-slate-800 via-slate-900 to-slate-800 text-white py-24 overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-r from-amber-500/10 to-yellow-500/10" />
        <div className="absolute top-20 right-20 w-64 h-64 bg-amber-500/20 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-20 w-64 h-64 bg-yellow-500/20 rounded-full blur-3xl" />
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <img src={logoUrl} alt="Logo" className="h-20 w-20 mx-auto mb-6 object-contain" />
            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
              Drive & Earn with J-Style Services
            </h1>
            <p className="text-xl text-slate-300 max-w-2xl mx-auto mb-8">
              Join thousands of drivers earning on their own schedule. Set your hours, maximize your income, and be your own boss.
            </p>
            <div className="flex gap-4 justify-center flex-wrap">
              <Button 
                size="lg" 
                onClick={() => navigate('/driver/signup')}
                className="bg-gradient-to-r from-amber-500 to-yellow-600 hover:from-amber-600 hover:to-yellow-700 text-white px-10 py-7 text-lg rounded-xl shadow-lg hover:shadow-xl transition-all"
              >
                <Car className="mr-2" size={24} />
                Become a Driver
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                onClick={() => navigate('/driver/login')}
                className="bg-white text-slate-900 hover:bg-slate-100 border-2 px-10 py-7 text-lg rounded-xl"
              >
                Driver Login
              </Button>
            </div>
            <div className="pt-8 flex justify-center gap-8 text-center">
              <div>
                <p className="text-3xl font-bold text-amber-400">$2,500+</p>
                <p className="text-sm text-slate-400">Average monthly earnings</p>
              </div>
              <div>
                <p className="text-3xl font-bold text-amber-400">5,000+</p>
                <p className="text-sm text-slate-400">Active drivers</p>
              </div>
              <div>
                <p className="text-3xl font-bold text-amber-400">24-48h</p>
                <p className="text-sm text-slate-400">Approval time</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="container mx-auto px-4 py-20">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold mb-4">Why Drive with Us?</h2>
          <p className="text-xl text-gray-600">Everything you need to succeed on the road</p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {benefits.map((benefit, index) => {
            const Icon = benefit.icon;
            return (
              <Card key={index} className="p-6 hover:shadow-lg transition-all border-2 hover:border-amber-300">
                <div className="w-14 h-14 bg-gradient-to-br from-amber-100 to-yellow-100 rounded-xl flex items-center justify-center mb-4">
                  <Icon className="text-amber-600" size={28} />
                </div>
                <h3 className="text-xl font-bold mb-2">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </Card>
            );
          })}
        </div>
      </section>

      {/* How It Works */}
      <section className="bg-slate-50 py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">How to Get Started</h2>
            <p className="text-xl text-gray-600">Four simple steps to start earning</p>
          </div>
          <div className="grid md:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {steps.map((step) => (
              <div key={step.number} className="text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-amber-500 to-yellow-600 rounded-full flex items-center justify-center mx-auto mb-4 text-white text-2xl font-bold">
                  {step.number}
                </div>
                <h3 className="text-xl font-bold mb-2">{step.title}</h3>
                <p className="text-gray-600">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Requirements */}
      <section className="container mx-auto px-4 py-20">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Driver Requirements</h2>
            <p className="text-xl text-gray-600">Make sure you meet these requirements before applying</p>
          </div>
          <Card className="p-8 border-2">
            <div className="grid md:grid-cols-2 gap-4">
              {requirements.map((req, index) => (
                <div key={index} className="flex items-center gap-3">
                  <CheckCircle className="text-green-600 flex-shrink-0" size={24} />
                  <span className="text-lg">{req}</span>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </section>

      {/* CTA Section */}
      <section className="container mx-auto px-4 py-20">
        <Card className="bg-gradient-to-r from-slate-800 via-slate-900 to-slate-800 text-white p-12 md:p-16 text-center relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-r from-amber-500/10 to-yellow-500/10" />
          <div className="relative z-10 max-w-3xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">Ready to Start Earning?</h2>
            <p className="text-xl mb-8 text-slate-300">
              Join J-Style Services today and take control of your income. Apply now and get approved within 24-48 hours.
            </p>
            <Button 
              size="lg" 
              onClick={() => navigate('/driver/signup')}
              className="bg-gradient-to-r from-amber-500 to-yellow-600 hover:from-amber-600 hover:to-yellow-700 text-white px-12 py-7 text-xl rounded-xl shadow-lg hover:shadow-xl"
            >
              Apply to Drive Now
            </Button>
            <p className="mt-6 text-sm text-slate-400">
              Already have an account?{' '}
              <button onClick={() => navigate('/driver/login')} className="text-amber-400 hover:text-amber-300 font-medium underline">
                Sign in here
              </button>
            </p>
          </div>
        </Card>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <h4 className="font-bold mb-4 text-amber-400">For Drivers</h4>
              <ul className="space-y-2 text-gray-400">
                <li><button onClick={() => navigate('/driver/signup')} className="hover:text-amber-400 transition-colors">Apply Now</button></li>
                <li><button onClick={() => navigate('/driver/login')} className="hover:text-amber-400 transition-colors">Driver Login</button></li>
                <li><a href="#" className="hover:text-amber-400 transition-colors">Driver FAQ</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4 text-amber-400">Support</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-amber-400 transition-colors">Help Center</a></li>
                <li><a href="#" className="hover:text-amber-400 transition-colors">Safety</a></li>
                <li><a href="#" className="hover:text-amber-400 transition-colors">Contact Us</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4 text-amber-400">Company</h4>
              <ul className="space-y-2 text-gray-400">
                <li><button onClick={() => navigate('/')} className="hover:text-amber-400 transition-colors">Home</button></li>
                <li><a href="#" className="hover:text-amber-400 transition-colors">About Us</a></li>
                <li><a href="#" className="hover:text-amber-400 transition-colors">Careers</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4 text-amber-400">Legal</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-amber-400 transition-colors">Terms</a></li>
                <li><a href="#" className="hover:text-amber-400 transition-colors">Privacy</a></li>
                <li><a href="#" className="hover:text-amber-400 transition-colors">Driver Agreement</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2025 J-Style Services. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default DriversPage;
